import { useEffect } from 'react';

export type PageTitleEntry = { pattern: RegExp; title: string };

/**
 * Sets `document.title` to `{appName} - {pageName}` on every route change.
 *
 * Locale prefixes (e.g. `/de-de/`) are stripped before matching so that
 * `titleMap` entries only need to handle the base path.
 *
 * Falls back to `appName` alone when no entry matches.
 *
 * @param appName  - Application name shown in every tab title, e.g. `"Lucid Partner Portal"`
 * @param titleMap - Ordered list of pattern → page-name entries; first match wins.
 * @param pathname - Current route pathname. Pass `useLocation().pathname` from the
 *                   host app. Falls back to `window.location.pathname` if omitted.
 *
 * @example
 * const TITLE_MAP: PageTitleEntry[] = [
 *   { pattern: /\/prospect-desk/, title: 'Prospect Desk' },
 *   { pattern: /^\/?([a-z]{2}-[a-z]{2})?$/, title: 'Dashboard' },
 * ];
 *
 * // Inside a component rendered inside a Router:
 * const { pathname } = useLocation();
 * usePageTitle('Lucid Partner Portal', TITLE_MAP, pathname);
 */
const usePageTitle = (appName: string, titleMap: PageTitleEntry[], pathname?: string): void => {
  const currentPathname = pathname ?? window.location.pathname;

  useEffect(() => {
    const strippedPath = currentPathname.replace(/^\/[a-z]{2}-[a-z]{2}/, '') || '/';
    const match = titleMap.find(({ pattern }) => pattern.test(strippedPath));
    document.title = match ? `${appName} - ${match.title}` : appName;
  }, [currentPathname, appName, titleMap]);
};

export default usePageTitle;
